import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { useDisconnectMetaLeads } from '../../../../queries/Ads/useDisconnectMetaLeads';
import { useGetMetaAdAccounts } from '../../../../queries/Ads/useGetMetaAdAccounts';
import { invalidateMetaAdsProvider, useGetMetaAdsProvider } from '../../../../queries/Ads/useGetMetaAdsProvider';
import { useGetMetaPages } from '../../../../queries/Ads/useGetMetaPages';
import { Button } from '../../../../web/components-v2/Button';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { IModalProps, Modal } from '../../../../web/components/Modal';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { styleSheet } from './styles';

const disconnectConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{ isDestructive: true, representedObject: true, title: 'Disconnect Now' },
	{ isCancel: true, representedObject: false, title: 'Cancel' },
];

export interface IMetaAdsAdminModalProps<TResult = any> extends IModalProps<TResult> {
	impersonationContext: Api.IImpersonationContext;
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
}

const formatDate = (value?: string) => {
	if (!value) {
		return '—';
	}
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export const MetaAdsAdminModal = ({
	impersonationContext,
	isOpen,
	setIsOpen,
	...restProps
}: IMetaAdsAdminModalProps) => {
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('MetaAdsAdminModal');
	const [isConfirmingDisconnect, setIsConfirmingDisconnect] = React.useState(false);

	const providerQuery = useGetMetaAdsProvider({ impersonationContext, enabled: isOpen });
	const provider = providerQuery.data;
	const hasProviderLoaded = providerQuery.isSuccess;
	const isConnected = hasProviderLoaded && (provider?.enabled ?? false);

	const adAccountsQuery = useGetMetaAdAccounts({ impersonationContext, enabled: isOpen && isConnected });
	const pagesQuery = useGetMetaPages({ impersonationContext, enabled: isOpen && isConnected });

	const disconnectMutation = useDisconnectMetaLeads({
		impersonationContext,
		onSuccess: () => {
			toast.success('Meta Ads disconnected');
			invalidateMetaAdsProvider({ impersonationContext });
			setIsConfirmingDisconnect(false);
			setIsOpen(false);
		},
		onError: error => {
			logApiError('DisconnectMetaAds-Error', error);
			errorMessages.pushApiError(error, 'Failed to disconnect Meta Ads');
			setIsConfirmingDisconnect(false);
		},
	});

	const selectedAdAccountIds = new Set(provider?.adAccountIds ?? []);
	const selectedPageIds = new Set(provider?.pageIds ?? []);
	const selectedAdAccounts = (adAccountsQuery.data ?? []).filter(a => selectedAdAccountIds.has(a.id));
	const selectedPages = (pagesQuery.data ?? []).filter(p => selectedPageIds.has(p.id));

	const renderBody = () => {
		if (providerQuery.isPending) {
			return (
				<div className={css(styleSheet.loading)}>
					<LoadingSpinner type='small' />
				</div>
			);
		}

		if (providerQuery.isError) {
			return (
				<div className={css(styleSheet.statusRow)}>
					<span className={css(styleSheet.statusBadgeError)}>Error</span>
					<p className={css(styleSheet.helperText, styleSheet.errorText)}>
						Failed to load Meta Ads connection status. Please close this dialog and try again.
					</p>
				</div>
			);
		}

		if (!isConnected) {
			return (
				<div className={css(styleSheet.statusRow)}>
					<span className={css(styleSheet.statusBadgeDisabled)}>Disconnected</span>
					<p className={css(styleSheet.helperText)}>
						This account hasn't connected Meta Ads. Account users can connect from the Integrations page.
					</p>
				</div>
			);
		}

		return (
			<>
				<div className={css(styleSheet.statusRow)}>
					<span className={css(styleSheet.statusBadgeEnabled)}>Connected</span>
				</div>
				<dl className={css(styleSheet.detailsList)}>
					<dt>Connected at</dt>
					<dd>{formatDate(provider?.connectedAt)}</dd>
					<dt>Last poll</dt>
					<dd>{formatDate(provider?.lastPollTimestamp)}</dd>
					<dt>Last poll lead count</dt>
					<dd>{provider?.lastPollLeadCount ?? '—'}</dd>
					<dt>Last lead received</dt>
					<dd>{formatDate(provider?.lastLeadReceivedAt)}</dd>
					{provider?.lastPollError ? (
						<>
							<dt>Last poll error</dt>
							<dd className={css(styleSheet.errorText)}>{provider.lastPollError}</dd>
						</>
					) : null}
				</dl>
				<section className={css(styleSheet.section)}>
					<h4 className={css(styleSheet.sectionHeader)}>Selected Ad Accounts</h4>
					{adAccountsQuery.isLoading ? (
						<LoadingSpinner type='small' />
					) : selectedAdAccounts.length === 0 ? (
						<p className={css(styleSheet.helperText)}>No ad accounts selected.</p>
					) : (
						<ul className={css(styleSheet.list)}>
							{selectedAdAccounts.map(account => (
								<li key={account.id}>
									{account.name}
									{account.businessName ? (
										<span className={css(styleSheet.muted)}> · {account.businessName}</span>
									) : null}
								</li>
							))}
						</ul>
					)}
				</section>
				<section className={css(styleSheet.section)}>
					<h4 className={css(styleSheet.sectionHeader)}>Selected Pages</h4>
					{pagesQuery.isLoading ? (
						<LoadingSpinner type='small' />
					) : selectedPages.length === 0 ? (
						<p className={css(styleSheet.helperText)}>No pages selected.</p>
					) : (
						<ul className={css(styleSheet.list)}>
							{selectedPages.map(page => (
								<li key={page.id}>
									{page.name}
									{page.category ? <span className={css(styleSheet.muted)}> · {page.category}</span> : null}
								</li>
							))}
						</ul>
					)}
				</section>
			</>
		);
	};

	return (
		<>
			<Modal
				{...restProps}
				isOpen={isOpen}
				onRequestClose={() => setIsOpen(false)}
				shouldCloseOnOverlayClick={!disconnectMutation.isPending}
			>
				<div className={css(styleSheet.container)}>
					<header className={css(styleSheet.header)}>
						<h3 className={css(styleSheet.title)}>Meta Ads (Facebook &amp; Instagram)</h3>
					</header>
					<div className={css(styleSheet.body)}>{renderBody()}</div>
					<footer className={css(styleSheet.footer)}>
						<Button variant='secondary' size='sm' onClick={() => setIsOpen(false)}>
							Close
						</Button>
						{isConnected ? (
							<Button
								variant='destructive-secondary'
								size='sm'
								onClick={() => setIsConfirmingDisconnect(true)}
								disabled={disconnectMutation.isPending}
							>
								Disconnect
							</Button>
						) : null}
					</footer>
				</div>
			</Modal>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isConfirmingDisconnect,
					onRequestClose: (result, canceled) => {
						if (result?.isDestructive && !canceled) {
							disconnectMutation.mutate();
						} else {
							setIsConfirmingDisconnect(false);
						}
					},
				}}
				options={disconnectConfirmationOptions}
				title='Disconnect Meta Ads? This will stop syncing leads from Facebook and Instagram.'
			/>
		</>
	);
};
